/**
 * Lobby: seat count, names, house-rule options, start. [P5-hud]
 *
 * This is the local-hotseat lobby — "create room" collapses to "configure seats", since there is no
 * server in this build mode (see the lane brief's local dev mode requirement). Seat claiming for a
 * genuine hybrid room is future work once `apps/server` exists; what's here is deliberately the same
 * shape (seat count, names, colours, house rules) so wiring a real room later is additive.
 */

import { MAX_SEATS, MIN_SEATS, SEAT_COLOURS } from '@carc/protocol';
import { deleteSavedGame, describeAge, loadSavedGames, type SavedGame } from './saved-games.js';

export interface StartConfig {
  /** What the player calls this game, so they can find it on the shelf later. */
  readonly gameName: string;
  readonly seatNames: string[];
  /** Index into `SEAT_COLOURS`, one per seat. Distinct by construction — see the swatch picker. */
  readonly seatColours: number[];
  readonly stackCount: number;
  readonly turnSeconds: number;
}

export class Lobby {
  constructor(
    private readonly container: HTMLElement,
    private readonly onStart: (config: StartConfig) => void,
    /** Bring a saved game back. Optional so the lobby still works if resuming is unavailable. */
    private readonly onResume?: (saved: SavedGame) => void,
    /** Entry point into networked play. Rendered inside the panel because show() rebuilds the container. */
    private readonly onPlayOnline?: () => void,
    /** Opens the shared "How to play" rules panel. */
    private readonly onOpenHelp?: () => void,
  ) {}

  show(): void {
    this.container.classList.remove('hidden');
    this.container.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'lobby-panel panel';

    const heading = document.createElement('div');
    heading.className = 'lobby-heading';
    const h1 = document.createElement('h1');
    h1.textContent = 'Carcassonne 3D';
    heading.appendChild(h1);
    if (this.onOpenHelp) {
      const helpBtn = document.createElement('button');
      helpBtn.type = 'button';
      helpBtn.className = 'help-btn';
      helpBtn.textContent = '❓ How to play';
      helpBtn.addEventListener('click', this.onOpenHelp);
      heading.appendChild(helpBtn);
    }
    const subtitle = document.createElement('p');
    subtitle.className = 'subtitle';
    subtitle.textContent =
      'Everyone round one screen — set the seats up and pass the device along as you play.';
    panel.append(heading, subtitle);

    let seatCount = 2;

    const seatCountField = document.createElement('div');
    seatCountField.className = 'lobby-field';
    const seatCountLabel = document.createElement('label');
    seatCountLabel.textContent = 'Players';
    const stepper = document.createElement('div');
    stepper.className = 'stepper';
    const minus = button('−', () => setSeatCount(seatCount - 1));
    const output = document.createElement('output');
    const plus = button('+', () => setSeatCount(seatCount + 1));
    stepper.append(minus, output, plus);
    seatCountField.append(seatCountLabel, stepper);

    const seatList = document.createElement('div');
    seatList.className = 'seat-config-list';
    // Seat i starts on colour i; the swatch picker only ever moves a seat to a colour nobody holds.
    const seatColours: number[] = SEAT_COLOURS.map((_, i) => i);

    const setSeatCount = (n: number): void => {
      seatCount = Math.max(MIN_SEATS + 1, Math.min(MAX_SEATS, n));
      output.textContent = String(seatCount);
      renderSeatRows();
    };

    const renderSeatRows = (): void => {
      const existingNames = [...seatList.querySelectorAll<HTMLInputElement>('input')].map((i) => i.value);
      seatList.innerHTML = '';
      for (let i = 0; i < seatCount; i++) {
        const row = document.createElement('div');
        row.className = 'seat-config-row';

        const colourName = document.createElement('span');
        colourName.className = 'seat-meta';

        /**
         * The swatch IS the picker: clicking it takes the next colour nobody else has taken.
         *
         * A dropdown per seat would be the obvious thing and is worse — it needs a label, it opens a
         * menu over the panel, and it lets two players sit down as the same colour, which the board
         * then cannot tell apart. Cycling through the free colours only makes an invalid choice
         * unreachable rather than merely discouraged.
         */
        const swatch = document.createElement('button');
        swatch.type = 'button';
        swatch.className = 'seat-swatch';
        const paint = (): void => {
          const colour = SEAT_COLOURS[seatColours[i] ?? 0];
          swatch.style.background = colour?.hex ?? '#999';
          colourName.textContent = colour?.name ?? '';
          swatch.setAttribute('aria-label', `Seat ${i + 1} colour: ${colour?.name ?? ''}. Click for the next free colour.`);
        };
        swatch.addEventListener('click', () => {
          const taken = new Set(seatColours.filter((_, idx) => idx !== i && idx < seatCount));
          const current = seatColours[i] ?? 0;
          for (let step = 1; step <= SEAT_COLOURS.length; step++) {
            const next = (current + step) % SEAT_COLOURS.length;
            if (!taken.has(next)) {
              seatColours[i] = next;
              break;
            }
          }
          paint();
        });
        paint();

        const input = document.createElement('input');
        input.type = 'text';
        input.maxLength = 20;
        input.value = existingNames[i] || `Player ${i + 1}`;
        input.setAttribute('aria-label', `Seat ${i + 1} name`);

        row.append(swatch, input, colourName);
        seatList.appendChild(row);
      }
    };

    const stackField = document.createElement('div');
    stackField.className = 'lobby-field';
    const stackLabel = document.createElement('label');
    stackLabel.textContent = "Draw stacks (Tony's house rule — off = standard play)";
    let stackCount = 1;
    const stackStepper = document.createElement('div');
    stackStepper.className = 'stepper';
    const stackOutput = document.createElement('output');
    stackOutput.textContent = '1';
    const stackMinus = button('−', () => setStackCount(stackCount - 1));
    const stackPlus = button('+', () => setStackCount(stackCount + 1));
    stackStepper.append(stackMinus, stackOutput, stackPlus);
    stackField.append(stackLabel, stackStepper);
    const setStackCount = (n: number): void => {
      stackCount = Math.max(1, Math.min(6, n));
      stackOutput.textContent = String(stackCount);
    };

    const timerField = document.createElement('div');
    timerField.className = 'lobby-field';
    const timerLabel = document.createElement('label');
    timerLabel.textContent = 'Turn timer (per turn — when it runs out the tile is placed for you)';
    const timerSelect = document.createElement('select');
    for (const [label, value] of [
      ['Off', 0],
      ['2 minutes', 120],
      ['5 minutes', 300],
    ] as const) {
      const opt = document.createElement('option');
      opt.value = String(value);
      opt.textContent = label;
      timerSelect.appendChild(opt);
    }
    timerField.append(timerLabel, timerSelect);

    const nameField = document.createElement('div');
    nameField.className = 'lobby-field';
    const nameLabel = document.createElement('label');
    nameLabel.textContent = 'Game name (so you can come back to it)';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.maxLength = 40;
    nameInput.placeholder = 'Kitchen table, Tuesday…';
    nameField.append(nameLabel, nameInput);

    const actions = document.createElement('div');
    actions.className = 'lobby-actions';
    const startBtn = document.createElement('button');
    startBtn.type = 'button';
    startBtn.className = 'primary';
    startBtn.textContent = 'Start game';
    startBtn.addEventListener('click', () => {
      // One game per press. Building a session takes long enough that a double-tap — easy on a phone —
      // used to start two, and the second one's board and pointer handlers stacked on top of the
      // first's. The teardown in `app.ts` makes that survivable; not firing twice makes it moot.
      if (startBtn.disabled) return;
      startBtn.disabled = true;
      const names = [...seatList.querySelectorAll<HTMLInputElement>('input')].map((i, idx) => i.value.trim() || `Player ${idx + 1}`);
      this.onStart({
        gameName: nameInput.value,
        seatNames: names,
        seatColours: seatColours.slice(0, names.length),
        stackCount,
        turnSeconds: Number(timerSelect.value),
      });
    });
    actions.appendChild(startBtn);

    setSeatCount(seatCount);
    panel.append(seatCountField, seatList, stackField, timerField, nameField, actions);

    // The shelf of games in progress. Rendered last so a fresh browser sees nothing extra, and so
    // resuming is one click rather than a menu.
    const resumeSection = this.buildResumeSection();
    if (resumeSection) panel.appendChild(resumeSection);

    if (this.onPlayOnline) {
      const online = document.createElement('button');
      online.className = 'play-online-btn';
      online.textContent = 'Play online — other devices, or against AI';
      online.onclick = () => this.onPlayOnline?.();
      panel.appendChild(online);

      // Say the AI is there. It always has been — you set a seat to AI in the online lobby — but
      // nothing on this screen mentioned it, so unless you already knew, you did not know. Tony's
      // point exactly: he is fine with it living on the online path, not with it being invisible.
      const aiNote = document.createElement('p');
      aiNote.className = 'lobby-note';
      aiNote.textContent =
        'You do not have to play yourself. Open Play online and set any seat to AI (easy, steady or sharp) — a room of one human and two AI opponents works fine, and you can still share the code if someone wants to join.';
      panel.appendChild(aiNote);
    }

    this.container.appendChild(panel);
  }

  /** The saved-games shelf, or null when there is nothing to show. */
  private buildResumeSection(): HTMLElement | null {
    if (!this.onResume) return null;
    const saved = loadSavedGames().filter((g) => !g.finished);
    if (saved.length === 0) return null;

    const section = document.createElement('div');
    section.className = 'lobby-field resume-section';
    const label = document.createElement('label');
    label.textContent = 'Games in progress';
    section.appendChild(label);

    const now = Date.now();
    for (const game of saved) {
      const row = document.createElement('div');
      row.className = 'resume-row';

      const info = document.createElement('div');
      info.className = 'resume-info';
      const title = document.createElement('div');
      title.className = 'resume-name';
      title.textContent = game.name;
      const meta = document.createElement('div');
      meta.className = 'seat-meta';
      const who = game.seats.map((s, i) => `${s.name} ${game.scores[i] ?? 0}`).join(' · ');
      meta.textContent = `${game.tilesPlaced} tiles · ${who} · ${describeAge(game.savedAt, now)}`;
      info.append(title, meta);

      const resumeBtn = document.createElement('button');
      resumeBtn.type = 'button';
      resumeBtn.className = 'primary';
      resumeBtn.textContent = 'Resume';
      resumeBtn.addEventListener('click', () => this.onResume?.(game));

      const deleteBtn = document.createElement('button');
      deleteBtn.type = 'button';
      deleteBtn.className = 'ghost';
      deleteBtn.textContent = 'Delete';
      deleteBtn.setAttribute('aria-label', `Delete saved game ${game.name}`);
      deleteBtn.addEventListener('click', () => {
        deleteSavedGame(game.id);
        this.show(); // rebuild, so the shelf matches what is actually stored
      });

      row.append(info, resumeBtn, deleteBtn);
      section.appendChild(row);
    }
    return section;
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.container.innerHTML = '';
  }

  showGameOver(finalScores: readonly number[], winners: readonly number[], seatNames: readonly string[], onPlayAgain: () => void): void {
    this.container.classList.remove('hidden');
    this.container.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'lobby-panel game-over-panel panel';

    const h1 = document.createElement('h1');
    const winnerNames = winners.map((i) => seatNames[i] ?? `Seat ${i + 1}`).join(' & ');
    h1.textContent = winners.length > 1 ? `It's a tie — ${winnerNames}!` : `${winnerNames} wins!`;
    panel.appendChild(h1);

    const scores = document.createElement('div');
    scores.className = 'game-over-scores';
    const order = finalScores.map((s, i) => ({ s, i })).sort((a, b) => b.s - a.s);
    for (const { s, i } of order) {
      const row = document.createElement('div');
      row.className = 'row' + (winners.includes(i) ? ' winner' : '');
      const dot = document.createElement('span');
      dot.className = 'seat-dot';
      dot.style.background = SEAT_COLOURS[i % SEAT_COLOURS.length]?.hex ?? '#999';
      const name = document.createElement('span');
      name.textContent = seatNames[i] ?? `Seat ${i + 1}`;
      const score = document.createElement('span');
      score.textContent = String(s);
      row.append(dot, name, score);
      scores.appendChild(row);
    }
    panel.appendChild(scores);

    const nameField = document.createElement('div');
    nameField.className = 'lobby-field';
    const nameLabel = document.createElement('label');
    nameLabel.textContent = 'Game name (so you can come back to it)';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.maxLength = 40;
    nameInput.placeholder = 'Kitchen table, Tuesday…';
    nameField.append(nameLabel, nameInput);

    const actions = document.createElement('div');
    actions.className = 'lobby-actions';
    const again = document.createElement('button');
    again.type = 'button';
    again.className = 'primary';
    again.textContent = 'Play again';
    again.addEventListener('click', onPlayAgain);
    actions.appendChild(again);
    panel.appendChild(actions);

    this.container.appendChild(panel);
  }
}

function button(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}
